import React, { useState, useEffect } from "react";
import {
  FaEnvelope,
  FaPaperPlane,
  FaTimes,
  FaCheck,
  FaCoins,
  FaCalendarAlt,
} from "react-icons/fa";
import { createmail, AlluserList } from "../../api/endpoints";
import notify from "../../utils/toast";
import PageHeader from "../../components/PageHeader";

const AddMail = () => {
  const [loading, setLoading] = useState(false);
  const [users, setUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  
  const [formData, setFormData] = useState({
    mailType: "",
    receiverId: "",
    title: "",
    content: "",
    rewardType: "NONE",
    rewardAmount: 0,
    expiresAt: "",
  });

  const [errors, setErrors] = useState({});

  const mailTypes = [
    { value: "SYSTEM", label: "System Mail", description: "Visible to all users" },
    { value: "PERSONAL", label: "Personal Mail", description: "Send to specific user(s)" },
  ];

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      setLoadingUsers(true);
      const response = await AlluserList();
      console.log("Users response:", response);
      
      if (response.success) {
        let userList = [];
        if (Array.isArray(response.data)) {
          userList = response.data;
        } else if (response.data && Array.isArray(response.data.users)) {
          userList = response.data.users;
        } else if (response.data && Array.isArray(response.data.docs)) {
          userList = response.data.docs;
        } else if (response.data && typeof response.data === 'object') {
          userList = Object.values(response.data).filter(item => 
            item && typeof item === 'object' && item._id
          );
        }
        setUsers(userList);
      } else {
        setUsers([]);
      }
    } catch (error) {
      console.error("Error fetching users:", error);
      setUsers([]);
    } finally {
      setLoadingUsers(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.mailType) newErrors.mailType = "Mail type is required";
    if (!formData.title.trim()) newErrors.title = "Title is required";
    if (!formData.content.trim()) newErrors.content = "Content is required";
    
    if (formData.mailType === "PERSONAL" && !formData.receiverId) {
      newErrors.receiverId = "Please select a user";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) return;

    try {
      setLoading(true);
      
      const payload = {
        title: formData.title,
        content: formData.content,
        mail_type: formData.mailType,
        reward_type: formData.rewardType || "NONE",
        reward_amount: Number(formData.rewardAmount) || 0,
      };

      if (formData.mailType === "PERSONAL") {
        payload.receiver_id = formData.receiverId;
      }

      if (formData.expiresAt) {
        payload.expires_at = formData.expiresAt;
      }

      console.log("=== SENDING PAYLOAD ===");
      console.log("Payload:", JSON.stringify(payload, null, 2));
      console.log("Payload keys:", Object.keys(payload));

      const response = await createmail(payload);
      
      console.log("=== RESPONSE ===");
      console.log("Response:", response);

      if (response.success) {
        notify.success("Your mail is on its way to players.", "Mail sent");
        setFormData({
          mailType: "",
          receiverId: "",
          title: "",
          content: "",
          rewardType: "NONE",
          rewardAmount: 0,
          expiresAt: "",
        });
      } else {
        notify.error(response.message, "Could not send this mail");
      }
    } catch (error) {
      console.error("=== ERROR ===");
      console.error("Error:", error);
      console.error("Error response:", error?.response);
      console.error("Error data:", error?.response?.data);
      notify.error(error, "Could not send this mail");
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    setFormData({
      mailType: "",
      receiverId: "",
      title: "",
      content: "",
      rewardType: "NONE",
      rewardAmount: 0,
      expiresAt: "",
    });
    setErrors({});
  };

  const getUserDisplayName = (user) => {
    const name = user.fullName || user.name || user.username || "";
    const email = user.email || "";
    
    if (name && email) {
      return `${name} (${email})`;
    } else if (name) {
      return name;
    } else if (email) {
      return email;
    } else {
      return user.mobile || "User";
    }
  };

  const renderReceiverField = () => {
    if (formData.mailType === "SYSTEM") {
      return (
        <div className="mb-3">
          <label className="form-label fw-semibold">
            Select User (Receiver) <span className="text-danger">*</span>
          </label>
          <input
            type="text"
            className="form-control"
            value="All Users"
            disabled
            style={{ backgroundColor: "#f8f9fa" }}
          />
          <small className="text-muted">System mail is visible to all users</small>
        </div>
      );
    }

    if (formData.mailType === "PERSONAL") {
      return (
        <div className="mb-3">
          <label className="form-label fw-semibold">
            Select User (Receiver) <span className="text-danger">*</span>
          </label>
          <select
            className={`form-select ${errors.receiverId ? "is-invalid" : ""}`}
            name="receiverId"
            value={formData.receiverId}
            onChange={handleChange}
            disabled={loadingUsers}
          >
            <option value="">Select User</option>
            {Array.isArray(users) && users.length > 0 ? (
              users.map((user) => (
                <option key={user._id || user.id} value={user._id || user.id}>
                  {getUserDisplayName(user)}
                </option>
              ))
            ) : (
              <option value="" disabled>No users found</option>
            )}
          </select>
          {loadingUsers && <small className="text-muted">Loading users...</small>}
          {errors.receiverId && (
            <div className="invalid-feedback">{errors.receiverId}</div>
          )}
        </div>
      );
    }

    return null;
  };

  const getMailTypeDescription = () => {
    const selected = mailTypes.find((t) => t.value === formData.mailType);
    return selected?.description || "";
  };

  return (
    <div className="add-mail-page lead-page">
      <div className="row">
        <div className="col-lg-12">
          <PageHeader
            title="Add Mail"
            subtitle="Create and send a system or personal mail"
          />

          <div className="card border-0 shadow-sm rounded-4">
            <div className="card-body p-4">
              <h5 className="fw-bold mb-4 d-flex align-items-center gap-2">
                <FaEnvelope className="text-primary" />
                Mail Information
              </h5>

              <form onSubmit={handleSubmit}>
                <div className="row">
                  <div className="col-md-6 mb-3">
                    <label className="form-label fw-semibold">
                      Mail Type <span className="text-danger">*</span>
                    </label>
                    <select
                      className={`form-select ${errors.mailType ? "is-invalid" : ""}`}
                      name="mailType"
                      value={formData.mailType}
                      onChange={handleChange}
                    >
                      <option value="">Select Mail Type</option>
                      {mailTypes.map((type) => (
                        <option key={type.value} value={type.value}>
                          {type.label}
                        </option>
                      ))}
                    </select>
                    {errors.mailType && (
                      <div className="invalid-feedback">{errors.mailType}</div>
                    )}
                    {formData.mailType && (
                      <small className="text-muted d-block mt-1">
                        <FaCheck className="text-success me-1" size={12} />
                        {getMailTypeDescription()}
                      </small>
                    )}
                  </div>

                  <div className="col-md-6 mb-3">
                    {renderReceiverField()}
                  </div>

                  <div className="col-md-12 mb-3">
                    <label className="form-label fw-semibold">
                      Title <span className="text-danger">*</span>
                    </label>
                    <input
                      type="text"
                      className={`form-control ${errors.title ? "is-invalid" : ""}`}
                      name="title"
                      placeholder="Enter mail title"
                      value={formData.title}
                      onChange={handleChange}
                    />
                    {errors.title && (
                      <div className="invalid-feedback">{errors.title}</div>
                    )}
                  </div>

                  <div className="col-md-12 mb-3">
                    <label className="form-label fw-semibold">
                      Content <span className="text-danger">*</span>
                    </label>
                    <textarea
                      className={`form-control ${errors.content ? "is-invalid" : ""}`}
                      name="content"
                      rows="6"
                      placeholder="Write mail content here..."
                      value={formData.content}
                      onChange={handleChange}
                    />
                    {errors.content && (
                      <div className="invalid-feedback">{errors.content}</div>
                    )}
                  </div>

                  <div className="col-md-6 mb-3">
                    <label className="form-label fw-semibold">
                      <FaCoins className="me-1" />
                      Reward Type
                    </label>
                    <select
                      className="form-select"
                      name="rewardType"
                      value={formData.rewardType}
                      onChange={handleChange}
                    >
                      <option value="NONE">None</option>
                      <option value="COINS">Coins</option>
                      <option value="BONUS">Bonus</option>
                      <option value="FREE_SPIN">Free Spin</option>
                    </select>
                  </div>

                  <div className="col-md-6 mb-3">
                    <label className="form-label fw-semibold">
                      <FaCoins className="me-1" />
                      Reward Amount
                    </label>
                    <input
                      type="number"
                      className="form-control"
                      name="rewardAmount"
                      placeholder="0"
                      value={formData.rewardAmount}
                      onChange={handleChange}
                      min="0"
                    />
                    <small className="text-muted">Leave 0 for no reward</small>
                  </div>

                  <div className="col-md-12 mb-3">
                    <label className="form-label fw-semibold">
                      <FaCalendarAlt className="me-1" />
                      Expires At (Optional)
                    </label>
                    <input
                      type="datetime-local"
                      className="form-control"
                      name="expiresAt"
                      value={formData.expiresAt}
                      onChange={handleChange}
                    />
                    <small className="text-muted">Mails will be auto deleted after 30 days</small>
                  </div>
                </div>

                <div className="d-flex gap-3 mt-4 pt-3 border-top">
                  <button
                    type="submit"
                    className="btn btn-primary px-4"
                    disabled={loading}
                  >
                    <FaPaperPlane className="me-2" />
                    {loading ? "Sending..." : "Send Mail"}
                  </button>
                  <button
                    type="button"
                    className="btn btn-outline-secondary px-4"
                    onClick={handleCancel}
                  >
                    <FaTimes className="me-2" />
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddMail;